import { h } from 'preact';
import Store from 'store/store';
import { accessNested, sortObjectByOrder, joinClasses } from 'utils/utils';
import { smoothScrollTo } from 'utils/smoothScroll';
import { isVideosPage } from 'utils/bolt';
import { getCopy } from 'utils/copy';

import Section from 'components/bolt/section/section';
import HTML from 'components/core/html/html';

import s from 'components/bolt/toutVertical/toutVertical.sss';

export default class ToutVertical extends Section {
	constructor(props) {
		super(props);

		this.pageData = props?.appData?.pages[props.id]?.data || {};
		this.toutStacks = sortObjectByOrder(this.pageData, (value, key) => {
			if (value && typeof value === 'object') {
				if (value.toJS) value = value.toJS();
				value.key = key;
			}
			return value;
		}).filter(item => typeof item !== 'string');
		this.filters = (this.pageData.filters || '').split(',').filter(e => e).map(item => item.trim());
		this.filters.unshift('all');

		this.state = {
			activeFilter: 'all',
			openDropdown: false,
			sticky: true
		};

		this.renderToutStack = this.renderToutStack.bind(this);
		this.sortTouts = this.sortTouts.bind(this);
		this.toggleDropdownOpen = this.toggleDropdownOpen.bind(this);
		this.renderTout = this.renderTout.bind(this);
	}

	openShowtimesAndFilterForMovieId = async (movieId) => {
		await import('store/bolt/showtimes');
		Store.emit('SET_FILTERS_SELECTED_MOVIE_ID', [movieId]);
		Store.emit('SHOWTIMES_REFRESH');
		Store.emit('SHOWTIMES_SHOW', true);
	};

	componentDidMount() {
		super.componentDidMount();

		if (window.IntersectionObserver) {
			let options = {
				root: document.getElementById('mainScroll'),
				rootMargin: '0px 0px -100%'
			};

			this.observer = new IntersectionObserver(([e]) => {
				if (this.state.sticky === e.isIntersecting && e.boundingClientRect?.y >= 0) {
					this.setState({ sticky: !e.isIntersecting });
				}
			}, options);

			if (this.filtersMenu) {
				this.observer.observe(this.filtersMenu);
			}
		}
	}

	componentWillUnmount() {
		if (this.observer && this.filtersMenu) {
			this.observer.unobserve(this.filtersMenu);
		}
	}

	triggerButtonClick(data) {
		const { path, title, button, sameWindow } = data;
		Store.emit('TOUT_CLICK', 'vertical', 'tout', `${title} - ${button}`);
		if (path.startsWith('http')) {
			let link = document.createElement('a');
			link.href = path;
			link.target = sameWindow ? '_self' : '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else if (path.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', path);
		} else if (path.startsWith('tickets/?id=')) {
			const movieId = path.replace('tickets/?id=', '').trim();
			this.openShowtimesAndFilterForMovieId(movieId);
		} else if (isVideosPage(path)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', path);
		} else if (path === 'boltShowtimes') {
			return Store.emit('SHOWTIMES_SHOW', true);
		} else if (path === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		} else {
			const pathIsSection = accessNested(this.props, `appData.pages.${path}.section`, false);
			if (pathIsSection) {
				return Store.emit('CHANGE_ACTIVE_SECTION', path);
			} else {
				return Store.emit('CLOSE_AND_NAVIGATE', path);
			}
		}
	}

	toggleDropdownOpen() {
		const { openDropdown } = this.state;
		this.setState({ openDropdown: !openDropdown });
	}

	sortTouts(touts) {
		return sortObjectByOrder(touts);
	}

	renderStackTitle({ title }) {
		return title && <HTML tag="h3" class={joinClasses(s.title, 'h3')} content={title} markdown inline />;
	}

	renderStackDescription({ description }) {
		return description && <HTML tag="div" class={joinClasses(s.description, 'xlarge')} content={description} markdown />;
	}

	renderToutStack(data, i) {
		const sortedTouts = this.sortTouts(data.touts);

		return (
			<div class={s.stack} key={`stack-${data.key || i}`}>
				{this.renderStackTitle(data)}
				{this.renderStackDescription(data)}
				<div class={s.touts}>
					{sortedTouts.map(this.renderTout)}
				</div>
			</div>
		);
	}

	renderButton(data) {
		const button = data?.button;
		if (!button) return;
		return (
			<button
				class={joinClasses(s.button, 'mediumB', data.disableButton && s.disabled)}
				onClick={() => this.triggerButtonClick(data)}
			>
				{button.includes('$') ? getCopy(button) : button}
			</button>
		);
	}

	renderTout(data) {
		const { title, description, src, alt, filters = '' } = data;
		const filterItems = filters.split(',').filter(e => e).map(item => item.trim());
		const { activeFilter } = this.state;
		if ((filterItems.indexOf(activeFilter) <= -1) && (activeFilter !== 'all')) {
			return;
		}
		const isVideo = src?.endsWith('.mp4') || src?.endsWith('.webm');

		return (
			<div class={s.tout}>
				{!!src && isVideo ? (
					<video class={joinClasses(s.video, s.image)} src={src} autoplay loop muted playsinline />
				) : (
					<img crossOrigin="anonymous" class={s.image} src={src} alt={alt} />
				)}
				<div class={s.content}>
					{title && <HTML tag="h3" markdown content={title} class={joinClasses(s.toutTitle, 'h4')} />}
					{!!filterItems.length && (
						<div class={s.filters} key="filters">
							{filterItems.map(filter => <span class={joinClasses(s.filter, 'small')}>{filter}</span>)}
						</div>
					)}
					{description && (
						<HTML
							tag="h4"
							class={joinClasses(s.toutDescription, s.showGradient, 'large')}
							content={description}
							markdown
							key="description"
						/>
					)}
					{this.renderButton(data)}
				</div>
			</div>
		);
	}

	selectFilter(filter) {
		smoothScrollTo(document.getElementById('mainScroll'), this.$container);
		this.setState({ activeFilter: filter });
		this.toggleDropdownOpen();
	}

	render({ id, appData }, { activeFilter, openDropdown, sticky }) {
		const isSection = this.props?.appData?.pages[id]?.section || false;
		const hasFiltersMenu = this.filters.length > 1;
		const otherFilters = this.filters.filter(filter => filter !== activeFilter);
		const allFilter = appData?.copy?.baseCopy?.$ALL;

		return (
			<div class={joinClasses(s.wrapper, isSection && s.section, hasFiltersMenu && s.hasFiltersMenu)} data-section={id} ref={$ref => this.$container = $ref}>
				{this.renderBgContent()}
				{!!hasFiltersMenu && (
					<div class={s.filtersMenu} ref={ref => this.filtersMenu = ref} key="filters">
						<div class={joinClasses(s.dropdown, s.menuFilter)} onClick={this.toggleDropdownOpen}>
							{activeFilter === 'all' ? (allFilter || 'All') : activeFilter}
							<div class={joinClasses(s.arrowDown, openDropdown && s.up)} />
						</div>
						<div class={joinClasses(s.otherFilters, openDropdown && s.visible, !sticky && s.absolute)}>
							{otherFilters.map(filter => {
								return (
									<div class={s.menuFilter} onClick={() => this.selectFilter(filter)}>
										{filter === 'all' ? (allFilter || 'All') : filter}
									</div>
								);
							})}
						</div>
					</div>
				)}
				{this.toutStacks.map(this.renderToutStack)}
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
